import {Component,Inject,OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {ToastrService} from "ngx-toastr";
import {filter,finalize,first} from "rxjs/operators";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {Bareme} from "@domain/prestation/bareme";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {TypeBareme} from "@domain/prestation/TypeBareme";
import * as moment from "moment";

/**
 * Popin de création/modification d'un barème
 */
@Component({
	host: {'data-test-id': 'bareme-detail-popin'},
	templateUrl: './bareme-detail-popin.component.html'
})
export class BaremeDetailPopinComponent implements OnInit {
	/** Types de barème */
	protected readonly TypeBareme = TypeBareme;

	/** Barème */
	bareme: Bareme

	/** Chargement en cours */
	isPending: boolean;

	/** Flag indiquant que le barème est ouvert en modification */
	isEdition: boolean = false;

	/** Flag indiquant que le barème est ouvert avec un maxi à zéro (tranche la plus haute) */
	isLastTranche: boolean = false;

	/**
	 * Constructeur
	 */
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: {
			bareme: Bareme,
			typeBareme: TypeBareme,
			unite: string,
			mapLastBaremeByDate: Map<string,number>
		},
		private prestationService: PrestationService,
		private matDialogRef: MatDialogRef<BaremeDetailPopinComponent>,
		private toastrService: ToastrService,
		private translateService: TranslateService,
		private confirmService: ConfirmService
	) {}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//Récupération du barème
		this.bareme = new Bareme(this.data.bareme);

		//Si le barème est en modification
		this.isEdition = !!this.bareme.idBareme;

		//Si le barème est la tranche la plus haute
		this.isLastTranche = !this.bareme.maxi && !!this.bareme.idBareme;

		//Synchronisation de la date
		this.onDateChange();
	}

	/**
	 * Clic sur le bouton d'enregistrement
	 */
	onSave(): void {
		//Si le barème est la tranche la plus haute ou qu'on est sur une création de barème
		if (this.isLastTranche || (this.bareme.mini == 0 && !this.bareme.idBareme)) {
			//Sauvegarde du barème immédiate
			this.save();
		} else {
			//Sinon, sauvegarde du barème avec confirmation de l'utilisateur sur le risque de suppression
			this.confirmService.showConfirm(this.translateService.instant('admin.frais.prestations.detail.reglesGestion.remboursement.bareme.popin.alerteEnregistrement'))
				.pipe(filter(isConfirmed => isConfirmed))
				.subscribe({
					next: () => {
						//Sauvegarde du barème
						this.save();
					}
				});
		}
	}

	/**
	 * Enregistrement d'un barème
	 */
	private save(): void {
		//Chargement en cours
		this.isPending = true;

		//Appel au service
		this.prestationService.saveBareme(this.bareme,this.data.typeBareme)
			.pipe(first(),finalize(() => this.isPending = false))
			.subscribe((result: Result) => {
				//Vérification du code d'erreur
				if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
					//Toast succès
					this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

					//Fermeture de la popup
					this.matDialogRef.close(true);
				} else {
					//Gestion de l'erreur
					TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
				}
			});
	}

	/**
	 * Suppression d'un barème
	 */
	onDelete(): void {
		//Confirmation auprès de l'utilisateur sur la suppression d'un barème
		this.confirmService.showConfirm(this.translateService.instant('admin.frais.prestations.detail.reglesGestion.remboursement.bareme.popin.alerteSuppression'))
			.pipe(filter(isConfirmed => isConfirmed))
			.subscribe({
				next: () => {
					//Chargement en cours
					this.isPending = true;

					//Appel au service
					this.prestationService.deleteBareme(this.bareme.idBareme,this.data.typeBareme)
						.pipe(first(),finalize(() => this.isPending = false))
						.subscribe((result: Result) => {
							//Vérification du code d'erreur
							if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
								//Toast succès
								this.toastrService.success(this.translateService.instant('global.success.suppression'));

								//Fermeture de la popup
								this.matDialogRef.close(true);
							} else {
								//Gestion de l'erreur
								TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
							}
						});
				}
			});
	}

	/**
	 * Changement de la date
	 */
	onDateChange(): void {
		//Sortie du cycle standard pour forcer la prise en compte de la nouvelle valeur
		setTimeout(() => {
			//Si le barème n'a pas de date d'application
			if (!this.bareme.dateAppli) {
				//Forçage du mini à zéro
				this.bareme.mini = 0;
			} else if (!this.isEdition) {
				//Rend moment.js exploitable depuis les TUs
				const m = (moment as any).default ?? (moment as any);

				//Sinon, si le barème n'est pas en édition, récupération du dernier barème pour la date d'application fournie
				const lastBareme: number = this.data.mapLastBaremeByDate.get(m(this.bareme.dateAppli).toDate().toString());

				//Établissement du minimum correspondant
				this.bareme.mini = !!lastBareme ? lastBareme : 0;

				//On est forcément en création, donc en tranche la plus haute
				this.isLastTranche = true;
			}
		});
	}
}
